"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { vercelStegaSplit } from "@vercel/stega";
import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";

import colon from "@/public/trademania-2/count-colon.svg";

/** Fallback event start (13/10/2026 18:00 CEST) when the page has no eventDate yet. */
export const DEFAULT_EVENT_DATE = "2026-10-13T18:00:00+02:00";

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Scroll-linked frame callbacks, run once per frame by the page's frame loop.
 */
const scrollFrameSubs = new Set<() => void>();
export function onScrollFrame(fn: () => void) {
  scrollFrameSubs.add(fn);
  return () => void scrollFrameSubs.delete(fn);
}
export const runScrollFrame = () => scrollFrameSubs.forEach((fn) => fn());

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** ["first", "rest of the text"], used where the phone design boxes the words line by line. */
export const splitFirstWord = (text?: string): [string, string] => {
  const [first = "", ...rest] = stegaClean(text ?? "").trim().split(/\s+/);
  return [first, rest.join(" ")];
};

/** ["all but the last word", "last word"]. */
export const splitLastWord = (text?: string): [string, string] => {
  const words = stegaClean(text ?? "").trim().split(/\s+/);
  const last = words.pop() ?? "";
  return [words.join(" "), last];
};

export type CountdownParts = { d: string; h: string; m: string; s: string };

export const COUNTDOWN_UNITS: [keyof CountdownParts, string][] = [
  ["d", "TAGE"],
  ["h", "STD"],
  ["m", "MIN"],
  ["s", "SEK"],
];

/**
 * Live countdown to `target`. Renders "00" on the server and the first client paint so
 * hydration matches, then ticks every second.
 */
export function useCountdown(target?: string): CountdownParts {
  const [parts, setParts] = useState<CountdownParts>({ d: "00", h: "00", m: "00", s: "00" });

  useEffect(() => {
    const end = new Date(target || DEFAULT_EVENT_DATE).getTime();
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setParts({
        d: pad(Math.floor(left / 86400)),
        h: pad(Math.floor((left % 86400) / 3600)),
        m: pad(Math.floor((left % 3600) / 60)),
        s: pad(left % 60),
      });
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  return parts;
}

/** Adds `in` once the element enters the viewport (never removed). */
export function useInView<T extends HTMLElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    // safety net: never leave content hidden
    const id = window.setTimeout(() => setInView(true), 3000);
    return () => {
      io.disconnect();
      window.clearTimeout(id);
    };
  }, [threshold]);

  return { ref, inView };
}

/** Fade + 24px rise on enter (prototype `.fx-up`). */
export function FadeUp({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, inView } = useInView<HTMLDivElement>(0.15);
  return (
    <div
      ref={ref}
      className={cn(
        "transition-[opacity,transform] duration-1000 ease-[cubic-bezier(.22,1,.36,1)]",
        inView ? "translate-y-0 opacity-100" : "translate-y-[24px] opacity-0",
        className,
      )}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/**
 * Headline reveal (as on yunilabs.de), used as the heading's `ref` next to `data-reveal`: the
 * RevealWords words inside rise out of their masks line by line across the whole heading
 * (highlight boxes included) once 35% of it is in view. They drop back once the heading has
 * left the viewport, so the reveal replays on the way back. Styles: `.tm2-word`.
 */
export function revealLines(el: HTMLElement | null) {
  if (!el) return;

  // line index of every word (--l, the stagger), measured after layout so it follows the real
  // wrapping; words in the hidden phone/desktop variant have no boxes and are skipped
  const measure = () => {
    const step = parseFloat(getComputedStyle(el).fontSize) / 2;
    let top = -Infinity;
    let line = -1;
    el.querySelectorAll<HTMLElement>("[data-word]").forEach((word) => {
      const box = word.getClientRects()[0];
      if (!box) return;
      if (box.top - top > step) {
        line++;
        top = box.top;
      }
      word.style.setProperty("--l", String(line));
    });
  };
  const ro = new ResizeObserver(measure);
  ro.observe(el);
  document.fonts?.ready.then(measure);

  const io = new IntersectionObserver(
    ([entry]) => {
      if (entry.intersectionRatio >= 0.35) el.dataset.in = "";
      else if (!entry.isIntersecting) delete el.dataset.in;
    },
    { threshold: [0, 0.35] },
  );
  io.observe(el);

  return () => {
    ro.disconnect();
    io.disconnect();
  };
}

/** Splits a headline into words, each in its own mask; the heading reveals them (`revealLines`). */
export function RevealWords({ text, className }: { text?: string; className?: string }) {
  if (!text) return null;
  // keep the invisible stega payload (visual editing) out of the word split
  const { cleaned, encoded } = vercelStegaSplit(text);
  const words = cleaned.split(/(\s+)/);

  return (
    <span className={className}>
      {words.map((word, i) =>
        // whitespace is kept as is, so line breaks survive in `whitespace-pre-line` titles
        /^\s+$/.test(word) ? (
          word
        ) : word ? (
          <span
            key={i}
            data-word
            className="tm2-word">
            <span>{word}</span>
          </span>
        ) : null,
      )}
      {encoded && <span className="sr-only">{encoded}</span>}
    </span>
  );
}

/**
 * Highlight box behind a part of a headline: side borders only (Figma).
 * light = white fill + lime 3px, dark = white 8% fill + mint 1px.
 */
export function Highlight({
  children,
  theme = "light",
  className,
  boxClassName,
}: {
  children: React.ReactNode;
  theme?: "light" | "dark";
  className?: string;
  /** Overrides for the box itself (insets, border width, fill). */
  boxClassName?: string;
}) {
  return (
    <span className={cn("relative z-0 inline-block whitespace-nowrap", className)}>
      <span
        aria-hidden
        className={cn(
          "absolute inset-x-[-.14em] inset-y-[-.03em] -z-10 border-x border-solid lg:inset-x-[-.16em] lg:inset-y-[-.06em]",
          theme === "light"
            ? "border-x-[3px] border-[#9BE055] bg-white"
            : "border-[#D9F9B9] bg-white/[.08] backdrop-blur-[2.55px]",
          boxClassName,
        )}
      />
      {children}
    </span>
  );
}

/**
 * "/// LABEL" chip: slashes from lg up. On phones the Figma frame mixes both: three bars on the
 * Features and last-CTA chips, the same slashes on Ein System, Warum and Host (`slashes`).
 */
export function Chip({
  text,
  theme = "light",
  slashes = false,
  className,
}: {
  text?: string;
  theme?: "light" | "dark" | "glass";
  slashes?: boolean;
  className?: string;
}) {
  if (!text) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center whitespace-pre rounded-[3px] font-geist text-[1.75rem] font-medium uppercase tracking-[.02em]",
        slashes
          ? "px-[2rem] py-[1.25rem] leading-[2.125rem]"
          : "gap-[1.25rem] px-[1.75rem] py-[1.125rem] leading-[2rem] lg:gap-0 lg:px-[2rem] lg:py-[1.25rem] lg:leading-[2.125rem]",
        theme === "light" && "bg-black/[.04] text-[#0B0C0D]",
        theme === "dark" && "bg-white/[.14] text-white",
        theme === "glass" && "bg-white/[.06] text-white backdrop-blur-[16.95px] lg:backdrop-blur-[17.25px]",
        className,
      )}
    >
      {!slashes && (
        <span aria-hidden className="flex gap-[.375rem] lg:hidden">
          <span className="h-[1.5rem] w-[.25rem] bg-[#A3CC4B]/20" />
          <span className="h-[1.5rem] w-[.25rem] bg-[#A3CC4B]/50" />
          <span className="h-[1.5rem] w-[.25rem] bg-[#A3CC4B]" />
        </span>
      )}
      {/* flex drops whitespace-only text nodes, so the Figma spacing lives inside the spans */}
      <span>
        <span aria-hidden className={slashes ? "inline" : "hidden lg:inline"}>
          {" "}
          <span className="text-[#A3CC4B]/20">/</span>
          <span className="text-[#A3CC4B]/50">/</span>
          <span className="text-[#A3CC4B]">/</span>
          {"  "}
        </span>
        {text}
      </span>
    </span>
  );
}

/**
 * Fixed-size illustration (Figma px) scaled to its container width, so the mock UIs
 * keep their exact composition at any breakpoint.
 */
export function Artboard({
  width,
  height,
  className,
  children,
}: {
  width: number;
  height: number;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  return (
    <div
      ref={ref}
      className={cn("relative overflow-clip", className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ width, height, transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
}

/** Glass countdown used in the hero and the last CTA. */
export function CountdownBox({ label, target }: { label?: string; target?: string }) {
  const parts = useCountdown(target);

  return (
    <div className="flex w-full flex-col items-center gap-[2rem] rounded-[6px] border border-white/[.05] bg-white/[.08] px-[2rem] pb-[2rem] pt-[2.5rem] lg:w-[57.125rem] lg:gap-[2.75rem] lg:px-[2.25rem] lg:pb-[2.25rem] lg:pt-[3rem] lg:backdrop-blur-[96.9px]">
      {label && (
        <p className="text-center font-geist text-[2rem] font-medium leading-[2rem] text-[#F4F4ED] lg:text-[2.25rem] lg:leading-[1.75rem]">
          {label}
        </p>
      )}
      <span className="h-px w-full bg-white/20" />
      <div className="flex items-start gap-[1rem] lg:items-center lg:gap-[1.75rem]">
        {COUNTDOWN_UNITS.map(([key, unit], i) => (
          <React.Fragment key={key}>
            {i > 0 && (
              <>
                {/* phones: two round dots (Figma 4 × 35); desktop: the square colon vector */}
                <span aria-hidden className="flex h-[4.375rem] w-[.5rem] flex-col justify-end gap-[.875rem] lg:hidden">
                  <span className="h-[.5rem] w-[.5rem] rounded-full bg-white" />
                  <span className="h-[.5rem] w-[.5rem] rounded-full bg-white" />
                </span>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={colon.src}
                  alt=""
                  width={5.088}
                  height={49.376}
                  className="hidden h-[6.172rem] w-[.636rem] lg:block"
                />
              </>
            )}
            <div className="flex flex-col items-center gap-[1rem] lg:gap-[1.25rem]">
              <span className="flex h-[7.25rem] w-[7.75rem] items-center justify-center rounded-[6px] border border-white/[.06] bg-white/[.09] lg:h-auto lg:w-auto lg:min-w-[9.375rem] lg:px-[1.75rem] lg:py-[1.5rem]">
                <span className="font-bebas text-[5.75rem] leading-none text-white lg:text-[7rem] lg:leading-[5.54rem]">
                  {parts[key]}
                </span>
              </span>
              <span className="font-geist text-[1.75rem] font-medium leading-none text-white/50 lg:text-[2.25rem] lg:leading-[2.77rem]">
                {unit}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/**
 * Infinite marquee whose speed follows the scroll velocity (prototype `[data-marquee]`).
 * Children are rendered four times; the track loops every quarter.
 */
export function Marquee({
  children,
  speed = 0.6,
  className,
  trackClassName,
}: {
  children: React.ReactNode;
  speed?: number;
  className?: string;
  trackClassName?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || prefersReducedMotion()) return;
    let x = 0;
    let vel = 0;
    let lastY = window.scrollY;
    let raf = 0;
    const loop = () => {
      const dy = window.scrollY - lastY;
      lastY = window.scrollY;
      vel += (dy - vel) * 0.12;
      const quarter = track.scrollWidth / 4;
      x -= speed + Math.abs(vel) * 0.35;
      if (quarter > 0 && -x >= quarter) x += quarter;
      track.style.transform = `translate3d(${x}px,0,0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [speed]);

  return (
    <div className={cn("overflow-hidden", className)} aria-hidden>
      <div ref={trackRef} className={cn("flex w-max will-change-transform", trackClassName)}>
        {[0, 1, 2, 3].map((i) => (
          <React.Fragment key={i}>{children}</React.Fragment>
        ))}
      </div>
    </div>
  );
}
